import type { BulkDriftRow } from "../../hooks/api/subitemBulk.types";

/**
 * The confirm dialog's reading order for a bulk plan: item, then its subitems, then which of
 * their columns change and where each value travels.
 *
 * The drift route hands back ONE ROW PER COLUMN, because that is the unit the plan's counts are
 * in and the unit a paging cursor can be honest about. It is not the unit a person reads.
 * "Owner and Due date on Kickoff call, from Engineering Sprint to the other two boards" is one
 * thought, and this module folds the rows back into that shape without changing what they say.
 *
 * Client-side and per page, deliberately: the route is paged in 100-row slices, and an item
 * whose columns straddle a page boundary appears at the end of one page and the start of the
 * next. `groupDriftRows` is run over everything fetched so far, so once the second page is in
 * the two halves merge again. Only the not-yet-fetched tail is ever split, and the dialog says
 * how many rows remain, so the split is visible rather than hidden.
 */

/** One "From X → Y, Z" line under an entity: the columns that travel that exact route. */
export interface DriftRoute {
  sourceBoardId: string;
  targetBoardIds: string[];
  columns: Array<{ columnKey: string; columnTitle: string }>;
}

export interface DriftEntity {
  kind: BulkDriftRow["kind"];
  /** The subitem's sync id, or the parent's for a parent-item entry. */
  entityId: string;
  name: string;
  routes: DriftRoute[];
}

export interface DriftGroup {
  parentSyncId: string;
  parentName: string;
  /** The parent item's own entry first (when its own columns drift), then its subitems. */
  entities: DriftEntity[];
  /** Distinct (entity, column) pairs in this group — what the header's count names. */
  columnCount: number;
}

const routeKey = (row: BulkDriftRow): string =>
  `${row.sourceBoardId}->${[...row.targetBoardIds].sort().join(",")}`;

export const groupDriftRows = (rows: BulkDriftRow[]): DriftGroup[] => {
  const groups = new Map<string, DriftGroup>();
  const entitiesByKey = new Map<string, DriftEntity>();
  const routesByKey = new Map<string, DriftRoute>();

  rows.forEach((row) => {
    let group = groups.get(row.parentSyncId);
    if (!group) {
      group = { parentSyncId: row.parentSyncId, parentName: row.parentName, entities: [], columnCount: 0 };
      groups.set(row.parentSyncId, group);
    }

    const entityId = row.kind === "subitem" ? (row.subitemSyncId ?? row.parentSyncId) : row.parentSyncId;
    const entityKey = `${row.parentSyncId}/${row.kind}/${entityId}`;
    let entity = entitiesByKey.get(entityKey);
    if (!entity) {
      entity = {
        kind: row.kind,
        entityId,
        name: row.kind === "subitem" ? (row.subitemName ?? entityId) : row.parentName,
        routes: [],
      };
      entitiesByKey.set(entityKey, entity);
      // The parent's own entry reads first whatever order its rows arrived in, because a
      // subitem is read as belonging to the item above it.
      if (row.kind === "parentItem") group.entities.unshift(entity);
      else group.entities.push(entity);
    }

    const key = `${entityKey}#${routeKey(row)}`;
    let route = routesByKey.get(key);
    if (!route) {
      route = { sourceBoardId: row.sourceBoardId, targetBoardIds: row.targetBoardIds, columns: [] };
      routesByKey.set(key, route);
      entity.routes.push(route);
    }
    if (!route.columns.some((column) => column.columnKey === row.columnKey)) {
      route.columns.push({ columnKey: row.columnKey, columnTitle: row.columnTitle });
      group.columnCount += 1;
    }
  });

  return Array.from(groups.values());
};

/** "item + 2 subitems · 5 columns" — the header's summary of a group. */
export const describeGroup = (group: DriftGroup): string => {
  const hasParent = group.entities.some((entity) => entity.kind === "parentItem");
  const subitems = group.entities.filter((entity) => entity.kind === "subitem").length;
  const parts: string[] = [];
  if (hasParent) parts.push("item");
  if (subitems > 0) parts.push(`${subitems} ${subitems === 1 ? "subitem" : "subitems"}`);
  const columns = `${group.columnCount} ${group.columnCount === 1 ? "column" : "columns"}`;
  return `${parts.join(" + ")} · ${columns}`;
};
